import { Injectable } from '@angular/core';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { jwtDecode } from 'jwt-decode';
import packageInfo from '../../../../../package.json';
import { environment } from '../../../../environments/environment';
import { IPayloadToken } from 'src/app/interfaces/Global/PayloadToken';
import {
  MAX_EVENTS_PER_BATCH,
  TelemetryEvent,
  UploadAttemptContext,
  buildTelemetryEvent,
  isoUtc,
  toBatches,
} from 'src/app/utils/UploadTelemetry';

/** Campos que el servicio completa solo; el llamador no los pasa. */
type EmitInput = Omit<UploadAttemptContext, 'inspectionId' | 'network' | 'occurredAt'>;

@Injectable({
  providedIn: 'root',
})
export class UploadTelemetryService {
  /**
   * `fetch` con `keepalive`, no `sendBeacon`. La descripción de la historia pide beacon y está
   * superseída: `sendBeacon(url, data)` no admite cabeceras, así que el lote no podría llevar el
   * `Authorization` que el endpoint exige. El autor lo aceptó el 28-ago y no alcanzó a actualizar el
   * texto. `keepalive` sobrevive igual al cierre de la página, que era lo que aportaba beacon.
   */
  private static readonly ENDPOINT = `${environment.GATEWAY_HOST}/api/telemetry`;

  /** Ventana de agrupamiento. Corta para no perder eventos, larga para no pedir por cada foto. */
  private static readonly FLUSH_DELAY_MS = 5_000;

  /**
   * Tope de la cola local. El servidor limita a 120 lotes por minuto por inspección; una cola sin
   * techo con la red caída crecería hasta comerse la memoria del teléfono durante la inspección,
   * que es exactamente lo que la telemetría no puede permitirse: observa el proceso, no participa.
   */
  private static readonly MAX_QUEUED_EVENTS = 500;

  private queue: TelemetryEvent[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private dropped = 0;

  /**
   * Las cabeceras se resuelven UNA vez y se cachean. `Device.getInfo()` es asíncrono y en el vaciado
   * por cierre de página no hay tiempo de esperarlo: sin caché, el último lote saldría sin las
   * cabeceras de dispositivo y el servidor —que las lee de ahí y no del cuerpo— escribiría
   * `platform: null` justo en los eventos del final de la inspección.
   */
  private headers: Record<string, string> | null = null;
  private headersPromise: Promise<Record<string, string>> | null = null;

  /** Último estado de red conocido. Se refresca en background; nunca bloquea la emisión. */
  private network: string | undefined;

  constructor() {
    this.installLifecycleHooks();
    void this.resolveHeaders();
    void this.refreshNetwork();
  }

  /**
   * Encola un intento de captura y carga.
   *
   * **Nunca lanza y nunca espera.** Un fallo del emisor no puede romper la subida ni, peor, activar
   * el encolado de una foto que en realidad se subió bien: la telemetría observa el proceso y no
   * participa de él. Por eso encola en memoria y devuelve, en vez de emitir en el camino crítico.
   */
  public emit(input: EmitInput): void {
    try {
      const event = buildTelemetryEvent({
        ...input,
        inspectionId: this.inspectionId(),
        network: this.network,
        occurredAt: isoUtc(Date.now()),
      });
      if (!event) {
        return;
      }
      if (this.queue.length >= UploadTelemetryService.MAX_QUEUED_EVENTS) {
        // Se descarta el MÁS VIEJO, no el nuevo: con la red caída, los eventos recientes son los
        // que explican por qué está caída.
        this.queue.shift();
        this.dropped++;
      }
      this.queue.push(event);
      this.scheduleFlush();
    } catch (error) {
      console.warn('[TELEMETRY] No se pudo encolar el evento de subida:', error);
    }
  }

  /**
   * Vacía la cola. `immediate` la usa el cierre de la página: ahí no hay tiempo de esperar cabeceras
   * ni reintentos, se manda con lo que haya en memoria.
   */
  public async flush(immediate = false): Promise<void> {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.queue.length === 0) {
      return;
    }
    // Se saca de la cola ANTES de enviar y no se repone si el envío falla. Un lote perdido de
    // telemetría no puede convertirse en un reintento que compita con la subida de fotos por la
    // misma red degradada; el DoD pide expresamente que su pérdida no afecte la inspección.
    const pending = this.queue;
    this.queue = [];
    if (this.dropped > 0) {
      console.warn(`[TELEMETRY] ${this.dropped} evento(s) descartados por cola llena`);
      this.dropped = 0;
    }

    const headers = immediate
      ? this.headers
        ? { ...this.headers, Authorization: `Bearer ${this.jwt()}` }
        : this.minimalHeaders()
      : await this.resolveHeaders();

    for (const batch of toBatches(pending, MAX_EVENTS_PER_BATCH)) {
      this.send(batch, headers);
    }
  }

  /**
   * El envío no se espera: su resultado no cambia nada del lado del cliente y esperarlo pondría a
   * la telemetría en el camino crítico.
   *
   * Pero el recuento sí se **loguea**. El servidor responde 200 con `{accepted, rejected}` en vez de
   * un 204 mudo, justamente porque antes descartaba eventos inválidos y contestaba éxito igual: un
   * `rejected > 0` acá es la única señal, del lado que puede verla, de que el cliente está armando
   * mal el evento. Sin esto el defecto vuelve a ser invisible desde el navegador.
   */
  private send(events: TelemetryEvent[], headers: Record<string, string>): void {
    try {
      void fetch(UploadTelemetryService.ENDPOINT, {
        method: 'POST',
        keepalive: true,
        headers,
        body: JSON.stringify({ events }),
      })
        .then(async (response) => {
          if (!response.ok) {
            console.warn(`[TELEMETRY] El lote no fue aceptado: HTTP ${response.status}`);
            return;
          }
          const counts = await response.json().catch(() => null);
          if (counts && counts.rejected > 0) {
            console.warn('[TELEMETRY] El servidor descartó eventos del lote:', counts);
          }
        })
        .catch((error) => {
          console.warn('[TELEMETRY] No se pudo enviar el lote:', error);
        });
    } catch (error) {
      console.warn('[TELEMETRY] No se pudo enviar el lote:', error);
    }
  }

  private scheduleFlush(): void {
    if (this.flushTimer) {
      return;
    }
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      void this.flush();
    }, UploadTelemetryService.FLUSH_DELAY_MS);
  }

  /**
   * `pagehide` y `visibilitychange` a la vez, no uno u otro: en iOS la pestaña se congela sin pasar
   * por `beforeunload`, y ese es justamente el caso —el asegurado sale de la app a mitad de la
   * inspección— cuyos eventos más interesan.
   */
  private installLifecycleHooks(): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        void this.flush(true);
      }
    });
    window.addEventListener('pagehide', () => void this.flush(true));
  }

  private async resolveHeaders(): Promise<Record<string, string>> {
    if (!this.headers) {
      if (!this.headersPromise) {
        this.headersPromise = this.buildHeaders();
      }
      try {
        this.headers = await this.headersPromise;
      } catch {
        return this.minimalHeaders();
      }
    }
    return { ...this.headers, Authorization: `Bearer ${this.jwt()}` };
  }

  /**
   * Las mismas cabeceras que `ApiService.getHeaders`. El servidor arma `platform`, `phone_model`,
   * `phone_brand`, `phone_so`, `app_version` e `internet_provider` **de acá**, no del cuerpo del
   * evento: sin ellas la consulta por dispositivo que pide el DoD no agrupa por nada.
   */
  private async buildHeaders(): Promise<Record<string, string>> {
    const device = await Device.getInfo();
    const status = await Network.getStatus();
    this.network = status.connectionType;
    return {
      'Content-Type': 'application/json',
      'X-App-Name': packageInfo.name ?? 'LET Autoinspección PWA',
      'X-App-Version': packageInfo.version ?? '1.0.0',
      'X-Internet-Provider': status.connectionType ?? 'unknown',
      'X-Phone-Brand': device.manufacturer ?? 'unknown',
      'X-Phone-Model': device.model ?? 'unknown',
      'X-Phone-So': device.operatingSystem ?? 'unknown',
      'X-Platform': device.platform ?? 'unknown',
    };
  }

  private minimalHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.jwt()}`,
    };
  }

  private async refreshNetwork(): Promise<void> {
    try {
      this.network = (await Network.getStatus()).connectionType;
      await Network.addListener('networkStatusChange', (status) => {
        this.network = status.connectionType;
      });
    } catch {
      this.network = undefined;
    }
  }

  private jwt(): string {
    return localStorage.getItem('jwt') ?? '';
  }

  /** El id sale del JWT. Sin él no hay evento: el servidor lo exige como entero positivo. */
  private inspectionId(): number | undefined {
    const jwt = localStorage.getItem('jwt');
    if (!jwt) {
      return undefined;
    }
    try {
      const raw = Number(jwtDecode<IPayloadToken>(jwt).inspection_id);
      return Number.isInteger(raw) && raw > 0 ? raw : undefined;
    } catch {
      return undefined;
    }
  }
}
